import type { SimulatorChannel, SimulatorEvent } from '@/backendApis';

const QUOTE_MAX_CHARS = 40;

const quote = (text: string | undefined) => {
  if (!text) return 'an earlier message';
  const shortened =
    text.length > QUOTE_MAX_CHARS
      ? `${text.slice(0, QUOTE_MAX_CHARS)}...`
      : text;
  return `"${shortened}"`;
};

/** The text of each message in the log, by the message id replies and reactions point at. */
export const textsByMessageId = (events: SimulatorEvent[]) =>
  new Map(
    events.flatMap((event) =>
      event.message_id &&
      (event.kind === 'inbound' || event.kind === 'outbound')
        ? [[event.message_id, event.text] as const]
        : []
    )
  );

const filenameOf = (event: SimulatorEvent) =>
  event.attachment?.filename ??
  (event.file ? event.file.filename || event.file.content_type : null);

const describeMessage = (
  direction: string,
  event: SimulatorEvent,
  messageTexts: Map<string, string>
) => {
  const filename = filenameOf(event);
  return [
    `${direction}:`,
    event.text,
    filename && `[file ${filename}]`,
    event.link_preview && '[link preview]',
    event.reply_to &&
      `(reply to ${quote(messageTexts.get(event.reply_to.message_id))})`,
  ]
    .filter(Boolean)
    .join(' ');
};

/** One line of text for an event, as the session log shows it. */
export const describeEvent = (
  event: SimulatorEvent,
  messageTexts: Map<string, string>
): string => {
  const target = quote(
    event.message_id ? messageTexts.get(event.message_id) : undefined
  );
  switch (event.kind) {
    case 'inbound':
      return describeMessage('sent', event, messageTexts);
    case 'outbound':
      return describeMessage('received', event, messageTexts);
    case 'webhook_status':
      return `webhook answered ${event.webhook_status}`;
    case 'reaction':
      return event.emoji
        ? `reaction ${event.emoji} on ${target}`
        : `reaction cleared on ${target}`;
    case 'read':
      return event.message_id ? `read receipt for ${target}` : 'read receipt';
    case 'typing':
      return 'typing indicator';
    case 'contact_card':
      return 'contact card shared';
  }
};

export type ChatMessage = {
  event: SimulatorEvent;
  isFromDevice: boolean;
  filename: string | null;
  reaction: string | null;
  refusedWebhookStatus: number | null;
};

/** The messages one channel's device would show, each with the latest reaction on it. */
export const chatMessages = (
  events: SimulatorEvent[],
  channel: SimulatorChannel
): ChatMessage[] => {
  const reactions = new Map<string, string | null>();
  const refusedWebhooks = new Map<string, number>();
  for (const event of events) {
    if (!event.message_id) continue;
    if (event.kind === 'reaction') reactions.set(event.message_id, event.emoji);
    if (event.webhook_status !== null && event.webhook_status >= 400) {
      refusedWebhooks.set(event.message_id, event.webhook_status);
    }
  }
  return events
    .filter(
      (event) =>
        event.channel === channel &&
        (event.kind === 'inbound' || event.kind === 'outbound')
    )
    .map((event) => ({
      event,
      isFromDevice: event.kind === 'inbound',
      filename: filenameOf(event),
      reaction: event.message_id
        ? (reactions.get(event.message_id) ?? null)
        : null,
      refusedWebhookStatus: event.message_id
        ? (refusedWebhooks.get(event.message_id) ?? null)
        : null,
    }));
};
